import type { ReactNode } from 'react'
import * as Sentry from '@sentry/react'

/**
 * Isolates one marketing section, so a failure inside it costs that section
 * rather than the whole page.
 *
 * The homepage previously had a single boundary, at the app root. Anything that
 * threw while rendering — including purely decorative art — replaced the entire
 * front door with "Something broke", for a visitor who had come to read about
 * the product. That is the worst possible trade on a pre-launch marketing page:
 * the hero, the signup CTA and the news all still work, and none of them were
 * involved.
 *
 * It is not hypothetical, and it is not fully fixable by fixing our own code:
 * `Scoring` renders a recharts chart, and recharts sets an inline style on its
 * own `<svg>`. React writes inline styles through `element.style`, which a
 * browser with SVG turned off (Firefox's `svg.disabled`, set by several
 * privacy-hardening profiles) does not provide — so that section throws no
 * matter what we do to it.
 *
 * The fallback is deliberately `null`: a section that cannot render has nothing
 * useful to say, and an inline error panel between two bands of a landing page
 * reads worse than the section simply not being there. Sentry still gets the
 * error, tagged with the section, so a silent gap is never an unnoticed one.
 */
export function SectionBoundary({ name, children }: { name: string; children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      // Sentry's fallback prop rejects a bare null, so render an empty fragment
      // — same outcome, nothing in the page where the section would have been.
      fallback={<></>}
      beforeCapture={(scope) => {
        scope.setTag('section', name)
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
